import clsx from 'clsx'
import { useEffect, useMemo, useState } from 'react'
import Avatar from 'react-avatar'
import { TApi } from '../../../../shared/const'
import http from '../../../../shared/http'
import Btn from '../../../../UI/Btn'
import MiniHeader from '../../../../UI/MiniHeader'
import Table from '../../../../UI/Table'
import styles from './style.module.scss'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import Swal from 'sweetalert2'
import PropTypes from 'prop-types'
import { formatVienna } from '../../../../shared/datetime'

/**
 * Die selbst erfassten Wirtschaftsexpert:innen — **Altbestand**.
 *
 * Über das Formular hinter dieser Liste trug eine Schüler:in einen
 * vollständigen Personendatensatz über jemand anderen ein: Anrede, Titel, Name,
 * Geburtsdatum, Firma, Funktion, E-Mail, Telefon und Anschrift. Die genannte
 * Person wusste davon nichts, bis eine Mail bei ihr ankam, in der alles noch
 * einmal stand; wer den Link darin anklickte, bestätigte damit seine
 * E-Mail-Adresse — eine Einwilligung in die Verarbeitung war das nie.
 *
 * Seit 09.08.2026 wird hier **nichts mehr angelegt und nichts mehr bearbeitet**.
 * An dieser Stelle steht „Expert:in einladen": Die Klasse gibt nur die
 * E-Mail-Adresse an, alles Weitere trägt die Person selbst ein und stimmt dabei
 * selbst zu. Der Reiter bleibt, solange es Alteinträge gibt — die Junior
 * Company soll sehen, was über wen gespeichert ist, und es selbst wegnehmen
 * können. Was niemand bestätigt hat, löscht der nächtliche Lauf nach seiner
 * Frist; das Datum steht in der Liste.
 */
function CompanyExperts(props) {
	const { t } = useTranslation()
	const [countExperts, setCountExperts] = useState(0)
	const [page, setPage] = useState(0)
	const limit = 10
	const [isLoading, setLoading] = useState(false)
	const [sort, setSort] = useState({ name: 'name', direction: 'desc' })
	const [experts, setExperts] = useState([])

	const { onChanged } = props

	const handleSort = (name, direction) => {
		setSort({ name, direction })
	}

	const fetchExperts = offset => {
		http.get(TApi.STUDENT_EXPERTS, {
			params: {
				limit,
				offset,
				order: sort.name,
				direction: sort.direction
			}
		}).then(({ data }) => {
			setExperts(data.rows)
			setCountExperts(data.count)
		})
	}
	const deleteExpert = id => {
		setLoading(true)
		http.delete(TApi.STUDENT_CHANGE_EXPERTS(id))
			.then(() => {
				fetchExperts()
				// Der Reiter darüber verschwindet mit dem letzten Eintrag — er
				// muss davon erfahren.
				if (onChanged) onChanged()
			})
			.finally(() => setLoading(false))
	}

	useEffect(() => {
		fetchExperts(page * limit)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, sort])

	const handleButton = id => {
		Swal.fire({
			title: t('Are you sure?'),
			text: t('You will not be able to revert this!'),
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#3085d6',
			cancelButtonColor: '#d33',
			confirmButtonText: t('Yes, delete it!'),
			cancelButtonText: t('Cancel')
		}).then(result => {
			if (result.isConfirmed) {
				Swal.fire(t('The expert was deleted successfully'))
				deleteExpert(id)
			}
		})
	}

	const columns = useMemo(
		() => [
			{
				Header: 'Name',
				accessor: originalRow => {
					return {
						avatar: originalRow.avatar,
						firstName: originalRow.firstName,
						lastName: originalRow.lastName
					}
				},
				id: 'name',
				width: 200,
				// Avatar und Name stehen nebeneinander. Ohne Flex brach der Name unter
				// den Avatar um, und die harten Mindestbreiten schoben die letzte Spalte
				// aus dem Bild (#69 — die Ansicht ist wieder erreichbar).
				Cell: ({ row }) => (
					<div className={styles.nameCell}>
						<Avatar
							src={row.values.name.avatar}
							round
							size={32}
							name={`${row.values.name.firstName} ${row.values.name.lastName}`}
						/>
						<span className={styles.nameText}>
							{`${row.values.name.firstName} ${row.values.name.lastName}`}
						</span>
					</div>
				)
			},
			{
				Header: t('Company'),
				accessor: 'company',
				width: 160,
				Cell: ({ row }) => <span className={styles.cellText}>{row.values.company}</span>
			},
			{
				Header: t('Position'),
				accessor: 'position',
				width: 140,
				Cell: ({ row }) => <span className={styles.cellText}>{row.values.position}</span>
			},
			{
				Header: t('E-Mail'),
				accessor: 'email',
				width: 180,
				Cell: ({ row }) => <span className={styles.cellText}>{row.values.email}</span>
			},
			{
				Header: t('City'),
				accessor: 'city',
				width: 100,
				Cell: ({ row }) => <span className={styles.cellText}>{row.values.city}</span>
			},
			{
				Header: 'Status',
				accessor: 'emailConfirmed',
				width: 240,
				Cell: ({ row }) => {
					const eintrag = row.original

					return (
						<div className={styles.branchContainer}>
							<span className={styles.cellText}>
								{row.values.emailConfirmed ? (
									<div className={styles.accepted}>{t('Accepted')}</div>
								) : (
									<div className={styles.pending}>
										{eintrag.deleteDueAt
											? t('deletedOn', {
													date: formatVienna(eintrag.deleteDueAt, 'dd.MM.yyyy')
											  })
											: t('Pending')}
									</div>
								)}
							</span>
							{/*
							 * Bearbeiten gibt es nicht mehr: Es hiesse, weitere
							 * Angaben ueber eine dritte Person einzutragen. Wer
							 * mit dieser Person arbeiten will, laedt sie ein —
							 * der Knopf dafuer steht in der Kopfleiste.
							 */}
							{!props.isTeacherStudentView && (
								<Btn
									loading={isLoading}
									className={styles.deleteBtn}
									iconColor="#000"
									iconSize={{ w: 16, h: 16 }}
									icon={isLoading ? '' : 'delete'}
									theme="white"
									title={t('Delete')}
									onClick={() => {
										handleButton(row.original.id)
									}}
								/>
							)}
						</div>
					)
				}
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[experts, isLoading, t]
	)

	// Der Satz über der Liste erklärt, warum hier nichts mehr anzulegen ist und
	// was mit den Einträgen geschieht. Ohne ihn wäre der fehlende Knopf ein
	// Fehler und das Verschwinden der Zeilen ein Rätsel.
	const hinweis = <p className={styles.legacyNote}>{t('ownExpertsLegacyNote')}</p>

	/*
	 * Eingebettet (als Reiter unter "Wirtschaftsexperten") ohne eigene Kopfzeile:
	 * Titel und Reiterleiste stehen dort schon. Ueber die eigene Adresse
	 * `/company-experts` bleibt die Ansicht mit Kopfzeile erreichbar.
	 */
	if (props.embedded)
		return (
			<>
				<div className={styles.embeddedActions}>{hinweis}</div>
				<div className={clsx('container', styles.tableContainer)}>
					<Table
						columns={columns}
						handleSort={handleSort}
						items={experts}
						page={page}
						limit={limit}
						countAll={countExperts}
						onChange={setPage}
					/>
				</div>
			</>
		)

	return (
		<div className={styles.root}>
			<MiniHeader className={styles.header} title={t('Economic experts')} bg="#f8faff" />
			<div className={clsx('container', styles.tableContainer)}>
				{hinweis}
				<Table
					columns={columns}
					handleSort={handleSort}
					items={experts}
					page={page}
					limit={limit}
					countAll={countExperts}
					onChange={setPage}
				/>
			</div>
		</div>
	)
}
CompanyExperts.propTypes = {
	embedded: PropTypes.bool,
	onChanged: PropTypes.func,
	isTeacherStudentView: PropTypes.bool.isRequired
}

const mapStateToProps = state => ({
	isTeacherStudentView: state.user.isTeacherStudentView
})

export default connect(mapStateToProps)(CompanyExperts)
